import { log } from "@/lib/log";
/**
 * Production-Ready Frontend Stability Configuration
 * Implements current best practices for robust web apps
 */

// 1. Environment Detection
export const isDevelopment = process.env.NODE_ENV === "development";
export const isProduction = process.env.NODE_ENV === "production";

// 2. Global Error Handler
window.addEventListener("error", (event) => {
  console.error("🚨 Global Error:", event.error);

  // Send to monitoring service in production
  if (isProduction) {
    // errorTrackingService.captureException(event.error);
  }
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("🚨 Unhandled Promise Rejection:", event.reason);

  // Prevent the default behavior (logging to console)
  event.preventDefault();

  // Send to monitoring service in production
  if (isProduction) {
    // errorTrackingService.captureException(new Error(event.reason));
  }
});

// 3. Performance Monitoring
if (isProduction && "performance" in window) {
  // Monitor Core Web Vitals
  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      log.debug(
        `Performance: ${entry.name} - ${(entry as any).value || entry.duration}ms`,
      );

      // Send metrics to monitoring service
      // performanceService.track(entry.name, entry.value);
    });
  });

  observer.observe({ entryTypes: ["measure", "navigation", "resource"] });
}

// 4. Service Worker Registration
//
// Deliberately none here. registerSW.js, injected at build time, registers
// /service-worker.js at scope "/" - and a scope holds exactly one registration.
// This file used to register a second script at the same scope, which did not
// give the app two workers: it gave it one registration the two scripts fought
// over. The loser sat in "waiting" indefinitely, which is what made "Load new
// app version." do nothing at all.

// 5. Critical Resource Preloading
export const preloadCriticalResources = () => {
  const criticalRoutes = [
    "/dashboard",
    "/health-records",
    "/medications",
    "/profile",
  ];

  // Preload critical routes for faster navigation
  criticalRoutes.forEach((route) => {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = route;
    document.head.appendChild(link);
  });
};

// 6. Memory Usage Monitoring
export const monitorMemoryUsage = () => {
  if ("memory" in performance) {
    const memory: any = (performance as Record<string, unknown>).memory;
    const memoryInfo = {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
    };

    log.debug("Memory Usage:", memoryInfo);

    // Warn if memory usage is high
    const usagePercentage =
      (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
    if (usagePercentage > 80) {
      console.warn(
        "🚨 High memory usage detected:",
        usagePercentage.toFixed(2) + "%",
      );
    }

    return memoryInfo;
  }
};

// 7. Connection Quality Detection
export const detectConnectionQuality = () => {
  if ("connection" in navigator) {
    const connection = (navigator as any).connection;
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
    };
  }
  return null;
};

// 8. Automatic Retry Configuration
export const getRetryConfig = () => {
  const connectionQuality = detectConnectionQuality();

  if (connectionQuality?.effectiveType === "slow-2g") {
    return { maxRetries: 5, retryDelay: 2000 };
  } else if (connectionQuality?.effectiveType === "2g") {
    return { maxRetries: 3, retryDelay: 1500 };
  } else {
    return { maxRetries: 2, retryDelay: 1000 };
  }
};

// Declare global interface for AbortController
declare global {
  interface Window {
    globalAbortController?: AbortController;
  }
}

// 9. Resource Cleanup Utilities
export const cleanupResources = () => {
  // Cancel all pending requests
  if (window.globalAbortController) {
    window.globalAbortController.abort();
  }

  // Clear all timeouts and intervals (safer approach)
  const highestTimeoutId = setTimeout(() => {}, 0);
  clearTimeout(highestTimeoutId);

  const highestIntervalId = setInterval(() => {}, 1000);
  clearInterval(highestIntervalId);
};

// 10. Health Check System
export const performHealthCheck = async () => {
  const healthChecks = [
    {
      name: "Backend API",
      check: () =>
        fetch(
          `${import.meta.env.VITE_API_URL || "http://localhost:8088/api"}/health`.replace(
            "/api/health",
            "/health",
          ),
          { method: "HEAD" },
        ),
    },
    {
      name: "Local Storage",
      check: () => {
        localStorage.setItem("health-check", "test");
        const result = localStorage.getItem("health-check");
        localStorage.removeItem("health-check");
        return Promise.resolve(result === "test");
      },
    },
    {
      name: "Session Storage",
      check: () => {
        sessionStorage.setItem("health-check", "test");
        const result = sessionStorage.getItem("health-check");
        sessionStorage.removeItem("health-check");
        return Promise.resolve(result === "test");
      },
    },
  ];

  const results = await Promise.allSettled(
    healthChecks.map(async ({ name, check }) => {
      try {
        await check();
        return { name, status: "healthy" };
      } catch (error: any) {
        return { name, status: "unhealthy", error };
      }
    }),
  );

  return results.map((result, index) => ({
    serviceName: healthChecks[index]?.name || `check-${index}`,
    ...((
      result as PromiseFulfilledResult<{
        name: string;
        status: string;
        error?: unknown;
      }>
    ).value || { status: "failed" }),
  }));
};

// Initialize stability features
if (typeof window !== "undefined") {
  // Setup global abort controller
  window.globalAbortController = new AbortController();

  // Only run health check when user is authenticated (not on public landing page)
  if (import.meta.env.DEV && window.location.pathname !== "/") {
    performHealthCheck().then((results) => {
      log.debug("🏥 Health Check Results:", results);
    });
  }

  // Monitor memory usage every 30 seconds in development
  if (isDevelopment) {
    setInterval(monitorMemoryUsage, 30000);
  }
}

export default {
  preloadCriticalResources,
  monitorMemoryUsage,
  detectConnectionQuality,
  getRetryConfig,
  cleanupResources,
  performHealthCheck,
};
